import { Check, ChevronDown } from 'lucide-react';
import { type KeyboardEvent, type Ref, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

/** Una opción: `value` es el texto que se guarda o se envía (vacío incluido: «Todos», «Selecciona…»). */
export interface ChoiceOption {
  readonly value: string;
  readonly label: string;
}

/** `popover` deja la lista en la capa superior: no la recorta un `overflow: hidden` y, dentro de un `<dialog>` modal, queda encima y se puede usar. */
const supportsPopover = () => typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype.showPopover === 'function';

const LIST =
  'max-h-60 overflow-y-auto rounded-xl border border-border bg-card p-1 text-sm text-card-foreground shadow-lg outline-none';

/**
 * Selector propio en lugar del `<select>` nativo, con la lista abierta al estilo de la Power App
 * (`src/components/ui/select.tsx`, solo como referencia visual): fondo de tarjeta, la opción resaltada y la
 * elegida en `bg-accent` y una marca a la derecha de la elegida. Sin Radix ni dependencias.
 *
 * - El botón es un `combobox` (`aria-expanded`, `aria-controls`, `aria-activedescendant`) y lleva el `id`, así
 *   que el `<label htmlFor>` de siempre lo nombra. `className` son las clases del campo al que sustituye
 *   (alto, ancho, radio y borde); el ancho, si no es `w-full`, es el de la opción más larga, como el nativo.
 * - La lista (`listbox`, filas `option` con `aria-selected`) sale debajo del botón, al menos tan ancha como él.
 *   Con `popover` va en la capa superior, colocada junto al botón; sin él, se despliega en su sitio.
 * - Teclado: flechas, Inicio y Fin mueven la fila resaltada; Enter la elige; Escape cierra. El foco no sale
 *   del botón. Se cierra también al elegir con el ratón, al pulsar fuera o al salir con Tab.
 * - `onChange` solo se llama si el valor cambia, como el `change` del `<select>`.
 */
export function ChoiceList({
  id,
  value,
  options,
  onChange,
  onBlur,
  className,
  inline = false,
  ref,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: {
  id: string;
  value: string;
  options: readonly ChoiceOption[];
  onChange: (value: string) => void;
  onBlur?: () => void;
  className: string;
  /** Junto a otro texto en la misma línea (p. ej. el filtro de estado), en lugar de ocupar su propia línea. */
  inline?: boolean;
  /** Para react-hook-form: enfoca el campo con error. */
  ref?: Ref<HTMLButtonElement>;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}) {
  const listId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  // Nombre de la lista: el texto de la etiqueta del botón (`<label htmlFor>`), leído al abrir.
  const [listName, setListName] = useState<string | undefined>(undefined);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const optionId = (index: number) => `${listId}-${index}`;
  const last = options.length - 1;

  const setButton = (node: HTMLButtonElement | null) => {
    buttonRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  const show = (index: number) => {
    setActive(Math.max(Math.min(index, last), 0));
    setListName(buttonRef.current?.labels?.[0]?.textContent ?? undefined);
    setOpen(true);
  };
  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  };
  const choose = (index: number) => {
    const option = options[index];
    if (option && option.value !== value) onChange(option.value);
    close(true);
  };

  // Clic fuera del botón y de la lista: se cierra sin elegir.
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && wrapperRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', outside, true);
    return () => document.removeEventListener('pointerdown', outside, true);
  }, [open]);

  // Con `popover`: a la capa superior, debajo del botón y al menos tan ancha como él; se recoloca al desplazar.
  useLayoutEffect(() => {
    const list = listRef.current;
    const button = buttonRef.current;
    if (!open || !list || !button || !supportsPopover()) return;
    const place = () => {
      const box = button.getBoundingClientRect();
      list.style.top = `${box.bottom + 4}px`;
      list.style.left = `${box.left}px`;
      list.style.minWidth = `${box.width}px`;
    };
    list.showPopover();
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // La fila resaltada siempre a la vista dentro de la lista.
  useEffect(() => {
    const list = listRef.current;
    const option = list?.children[active];
    if (!list || !(option instanceof HTMLElement)) return;
    if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop;
    else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight;
  }, [open, active]);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const moves: Record<string, number> = open
      ? { ArrowDown: active + 1, ArrowUp: active - 1, Home: 0, End: last }
      : { ArrowDown: selectedIndex < 0 ? 0 : selectedIndex, ArrowUp: selectedIndex < 0 ? 0 : selectedIndex, Home: 0, End: last };
    const target = moves[event.key];
    if (target !== undefined) {
      event.preventDefault();
      show(target);
    } else if (open && event.key === 'Enter') {
      event.preventDefault();
      choose(active);
    } else if (open && event.key === 'Escape') {
      // Solo cierra la lista: ni el `<dialog>` que la contiene ni otra ventana reciben este Escape.
      event.preventDefault();
      event.stopPropagation();
      close(true);
    } else if (open && event.key === 'Tab') {
      setOpen(false);
    }
  };

  const popover = supportsPopover();
  const selected = options[selectedIndex];

  return (
    <div ref={wrapperRef} className={inline ? 'relative inline-block' : 'relative'}>
      <button
        ref={setButton}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && options[active] ? optionId(active) : undefined}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        onClick={() => (open ? close(true) : show(selectedIndex))}
        onKeyDown={onKeyDown}
        onBlur={() => {
          setOpen(false);
          onBlur?.();
        }}
        className={`${className} cursor-default text-left`}
      >
        <span className="flex items-center justify-between gap-2">
          <span className="grid min-w-0">
            <span className="truncate [grid-area:1/1]">{selected?.label ?? ''}</span>
            {/* Ancho de la opción más larga, como el `<select>`: texto en `::after`, fuera del contenido del botón. */}
            {options.map((option, index) => (
              <span key={index} aria-hidden data-label={option.label} className="invisible h-0 truncate [grid-area:1/1] after:content-[attr(data-label)]" />
            ))}
          </span>
          <ChevronDown aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        </span>
      </button>
      {open ? (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={listName}
          popover={popover ? 'manual' : undefined}
          // El foco se queda en el botón: pulsar una fila o la barra de desplazamiento no lo mueve.
          onMouseDown={(event) => event.preventDefault()}
          className={popover ? `fixed inset-auto m-0 ${LIST}` : `absolute top-full left-0 z-50 mt-1 min-w-full ${LIST}`}
        >
          {options.map((option, index) => {
            const isSelected = index === selectedIndex;
            return (
              <li
                key={index}
                id={optionId(index)}
                role="option"
                aria-selected={isSelected}
                onPointerMove={() => setActive(index)}
                onClick={() => choose(index)}
                className={`flex cursor-default items-center justify-between gap-3 rounded-md px-2.5 py-1.5 ${
                  index === active || isSelected ? 'bg-accent text-accent-foreground' : ''
                } ${isSelected ? 'font-medium' : ''}`}
              >
                <span className="truncate">{option.label}</span>
                {isSelected ? <Check aria-hidden className="size-4 shrink-0" /> : <span aria-hidden className="size-4 shrink-0" />}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
